import { format, isToday, isYesterday, isThisWeek, isThisMonth, isThisYear } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

// Format a date with smart relative time
export const formatDate = (date: Date | string): string => {
  // Handle null/undefined/empty values
  if (!date) {
    return 'N/A';
  }
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }
  
  try {
    if (isToday(dateObj)) {
      return `Today, ${format(dateObj, 'h:mm a')}`;
    } else if (isYesterday(dateObj)) {
      return `Yesterday, ${format(dateObj, 'h:mm a')}`;
    } else if (isThisWeek(dateObj)) {
      return format(dateObj, 'EEEE, h:mm a');
    } else if (isThisMonth(dateObj)) {
      return format(dateObj, 'MMMM d, h:mm a');
    } else if (isThisYear(dateObj)) {
      return format(dateObj, 'MMMM d');
    } else {
      return format(dateObj, 'MMM d, yyyy');
    }
  } catch (error) {
    console.warn('Error formatting date:', date, error);
    return 'Invalid Date';
  }
};

// Format a date for input fields
export const formatDateForInput = (date: Date | string): string => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'yyyy-MM-dd');
};

// Format a datetime for datetime-local input fields
export const formatDateTimeForInput = (date: Date | string): string => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, "yyyy-MM-dd'T'HH:mm");
};

// Convert datetime-local value and timezone to ISO 8601 timestamp
export const formatTimestampForBackend = (datetimeLocal: string, timezone: string): string => {
  // Parse the datetime-local value as a date in the specified timezone
  // The datetime-local input provides a date/time without timezone info
  const zonedDate = new Date(datetimeLocal);
  
  // Convert the datetime from the specified timezone to UTC
  const utcDate = fromZonedTime(zonedDate, timezone);
  
  // Return the UTC ISO string for backend storage
  return utcDate.toISOString();
};

// Format a percentage
export const formatPercentage = (value: number, total: number): string => {
  if (total === 0) return '0%';
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(1)}%`;
};

// Format a number with commas
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Calculate the percentage of a value compared to a total
export const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return (value / total) * 100;
};

// Truncate text with ellipsis
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

// Format a currency amount
export const formatCurrency = (
  amount: number, 
  currencyCode = 'USD', 
  options: Intl.NumberFormatOptions = {}
): string => {
  // Defensive: fallback to USD if currencyCode is missing or invalid
  let safeCode = currencyCode;
  if (!safeCode || typeof safeCode !== 'string' || safeCode.length !== 3) {
    safeCode = 'USD';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: safeCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options
    }).format(amount);
  } catch (e) {
    // Fallback to plain number if even this fails
    return `$${amount.toFixed(2)}`;
  }
};

// Format a large number in a readable way (e.g., 1.2K, 1.5M)
export const formatLargeNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  } else {
    return num.toString();
  }
};

// Format a time duration in months and days
export const formatDuration = (months: number): string => {
  const wholeMonths = Math.floor(months);
  const days = Math.round((months - wholeMonths) * 30);
  
  if (wholeMonths === 0) {
    return `${days} days`;
  } else if (days === 0) {
    return `${wholeMonths} ${wholeMonths === 1 ? 'month' : 'months'}`;
  } else {
    return `${wholeMonths} ${wholeMonths === 1 ? 'month' : 'months'}, ${days} ${days === 1 ? 'day' : 'days'}`;
  }
};
